import React, { useEffect, useMemo, useRef } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { useEditor, EditorContent } from '@tiptap/react';
import { useTheme } from '../../contexts/ThemeContext';
import {
  getMemberCardEffectCss,
  injectMemberCardEffectStyles,
} from './memberCardEffectStyles';
import {
  createWriterEditorExtensions,
  serializeWriterDoc,
} from '../../utils/writerEditorExtensions';
import { loadHtmlIntoWriterEditor } from '../../utils/writerEditorApi';

/**
 * Web rich-text editor (TipTap) for calendar rider / writer content.
 */
export function CalendarWriterEditor({
  value,
  onChange,
  placeholder = 'Write rider notes for this day…',
  theme,
  editorRef: externalRef,
  isDarkMode: isDarkModeProp,
  editorMinHeight = 220,
  editorMaxHeight = 420,
  cellMode = false,
  instanceKey = '',
}) {
  const { isDarkMode: ctxDark } = useTheme();
  const isDarkMode = isDarkModeProp ?? ctxDark;
  const lastHtmlRef = useRef('');
  const styles = useMemoStyles(theme);

  const extensions = useMemo(
    () => createWriterEditorExtensions({ placeholder }),
    [placeholder]
  );

  const editor = useEditor({
    extensions,
    content: '<p></p>',
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: 'vrc-calendar-writer-editor',
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = serializeWriterDoc(ed);
      lastHtmlRef.current = html;
      onChange?.(html);
    },
  });

  useEffect(() => {
    if (externalRef) {
      externalRef.current = editor;
    }
  }, [editor, externalRef]);

  useEffect(() => {
    injectMemberCardEffectStyles(isDarkMode);
  }, [isDarkMode]);

  useEffect(() => {
    if (!editor) return;
    const next = String(value || '');
    if (next === lastHtmlRef.current) return;
    lastHtmlRef.current = next;
    loadHtmlIntoWriterEditor(editor, next);
  }, [value, editor]);

  const resolvedMinHeight = cellMode ? 44 : editorMinHeight;
  const resolvedMaxHeight = cellMode ? 120 : editorMaxHeight;

  const editorStyle = useMemo(
    () =>
      cellMode
        ? {
            minHeight: resolvedMinHeight,
            maxHeight: resolvedMaxHeight,
            overflowY: 'auto',
            padding: 0,
            border: 'none',
            borderRadius: 0,
            backgroundColor: 'transparent',
            color: theme.input.text,
            fontSize: 11,
            lineHeight: 1.45,
            outline: 'none',
          }
        : {
            minHeight: resolvedMinHeight,
            maxHeight: resolvedMaxHeight,
            overflowY: 'auto',
            padding: 12,
            borderRadius: 10,
            border: `1px solid ${theme.ui.border}`,
            backgroundColor: theme.background.secondary,
            color: theme.input.text,
            fontSize: 14,
            lineHeight: 1.55,
            outline: 'none',
          },
    [resolvedMinHeight, resolvedMaxHeight, theme, cellMode]
  );

  if (Platform.OS !== 'web') {
    return null;
  }

  return (
    <View style={styles.wrap}>
      <div
        key={instanceKey ? `writer-shell-${instanceKey}` : undefined}
        className={`vrc-calendar-writer-editor-shell${cellMode ? ' vrc-calendar-writer-editor-shell--cell' : ''}`}
        style={editorStyle}
      >
        <EditorContent editor={editor} />
      </div>
      <style>{`
        .vrc-calendar-writer-editor-shell .ProseMirror {
          min-height: inherit;
          outline: none;
        }
        .vrc-calendar-writer-editor {
          min-height: inherit;
          outline: none;
        }
        .ProseMirror p.is-empty:first-child::before {
          content: attr(data-placeholder);
          color: ${theme.input.placeholder};
          pointer-events: none;
          float: left;
          height: 0;
        }
        .ProseMirror-focused {
          outline: none;
        }
        .vrc-calendar-writer-editor h1 {
          font-size: 22px;
          font-weight: 800;
          margin: 12px 0 8px;
        }
        .vrc-calendar-writer-editor h2 {
          font-size: 17px;
          font-weight: 800;
          margin: 10px 0 6px;
        }
        .vrc-calendar-writer-editor p {
          margin: 0 0 8px;
        }
        .vrc-calendar-writer-editor ul {
          margin: 0 0 10px 18px;
          padding: 0;
        }
        .vrc-calendar-writer-editor a {
          color: ${theme.brand.accent};
          text-decoration: underline;
        }
        .vrc-calendar-writer-editor .vrc-writer-member-notes {
          min-height: 24px;
        }
        .vrc-calendar-writer-editor .vrc-writer-member-notes p {
          margin: 0;
          color: ${theme.text.secondary};
        }
        .vrc-calendar-writer-editor .vrc-writer-link-btn {
          display: inline-block;
          padding: 8px 14px;
          border-radius: 8px;
          background: ${theme.brand.primary};
          color: #fff;
          text-decoration: none;
          font-weight: 600;
        }
        .vrc-calendar-writer-editor-shell--cell .vrc-calendar-writer-editor h1 {
          font-size: 12px;
          margin: 4px 0 2px;
        }
        .vrc-calendar-writer-editor-shell--cell .vrc-calendar-writer-editor h2 {
          font-size: 11px;
          margin: 4px 0 2px;
        }
        .vrc-calendar-writer-editor-shell--cell .vrc-calendar-writer-editor p {
          margin: 0 0 4px;
          font-size: 11px;
          line-height: 1.45;
        }
        .vrc-calendar-writer-editor-shell--cell .vrc-calendar-writer-editor ul {
          margin: 0 0 4px 14px;
        }
        .vrc-calendar-writer-editor-shell--cell .ProseMirror p.is-empty:first-child::before {
          font-size: 11px;
        }
        ${getMemberCardEffectCss(isDarkMode)}
      `}</style>
    </View>
  );
}

function useMemoStyles(theme) {
  return StyleSheet.create({
    wrap: { width: '100%' },
  });
}

export default CalendarWriterEditor;
